export async function sha512Hex(input: string): Promise<string> {
  const encoded = new TextEncoder().encode(input);
  const hashBuffer = await crypto.subtle.digest("SHA-512", encoded);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Bosnian on the Monri form. Monri's locale is "ba-hr" (what the official
 * WooCommerce plugin offers as "Bosanski"); "ba" and "bs" silently fall back to
 * English (ipgtest, 2026-10-23 and 2026-11-03).
 */
export const MONRI_FORM_LANGUAGE = "ba-hr";

export interface MonriFormParams {
  url: string;
  authenticity_token: string;
  order_number: string;
  amount: number;
  currency: string;
  digest: string;
  transaction_type: string;
  success_url: string;
  cancel_url: string;
  /** Where Monri POSTs the signed payment notification (server-to-server). */
  callback_url?: string;
  /** Human description shown on the Monri page. Required by Monri (min 3 chars). */
  order_info?: string;
  /** Cardholder name prefill. Monri accepts 3-30 chars; see `monriCardholderFields`. */
  ch_full_name?: string;
  /** Cardholder email prefill. Monri accepts 3-100 chars; see `monriCardholderFields`. */
  ch_email?: string;
  /** Monri form language; defaults to MONRI_FORM_LANGUAGE ("ba-hr"). "bs"/"ba" fall back to English. */
  language?: string;
}

/**
 * Optional buyer prefill for the Monri form, clamped to Monri's field limits
 * (ch_full_name 3-30, ch_email 3-100). Monri rejects the whole form when a
 * buyer field is out of range, so a long name is cut to 30 characters and a
 * value that still does not fit is left out. An email is never cut: a shortened
 * address is a different address, so a too-long one is omitted.
 */
export function monriCardholderFields(
  fullName: string | null | undefined,
  email: string | null | undefined,
): Pick<MonriFormParams, "ch_full_name" | "ch_email"> {
  const out: Pick<MonriFormParams, "ch_full_name" | "ch_email"> = {};
  const name = (fullName ?? "").trim().replace(/\s+/g, " ").slice(0, 30).trim();
  if (name.length >= 3) out.ch_full_name = name;
  const mail = (email ?? "").trim();
  if (mail.length >= 3 && mail.length <= 100) out.ch_email = mail;
  return out;
}

export function submitMonriForm(monri: MonriFormParams): void {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = monri.url;
  const fields: Record<string, string | number> = {
    authenticity_token: monri.authenticity_token,
    order_number: monri.order_number,
    amount: monri.amount,
    currency: monri.currency,
    digest: monri.digest,
    transaction_type: monri.transaction_type,
    success_url_override: monri.success_url,
    cancel_url_override: monri.cancel_url,
    // Monri requires a non-empty order_info (min 3 chars); fall back to a label.
    order_info:
      monri.order_info && monri.order_info.trim().length >= 3
        ? monri.order_info.trim().slice(0, 100)
        : "Psihotron",
    language: monri.language || MONRI_FORM_LANGUAGE,
  };
  if (monri.callback_url) {
    fields.callback_url_override = monri.callback_url;
  }
  // Without ch_* Monri v3 makes the buyer type name and email by hand.
  if (monri.ch_full_name) fields.ch_full_name = monri.ch_full_name;
  if (monri.ch_email) fields.ch_email = monri.ch_email;
  for (const [k, v] of Object.entries(fields)) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = k;
    input.value = String(v);
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
}
